/**
 * Clickable table header for server-side sorting. Inactive columns show a faint
 * ↕ so it is obvious they can be clicked; the active one shows the direction.
 */
export function SortHeader({
  label,
  active,
  desc,
  onClick,
}: {
  label: string;
  active: boolean;
  desc: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-sort={active ? (desc ? 'descending' : 'ascending') : undefined}
      className={`inline-flex items-center gap-1 hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
    >
      {label}
      <span className={`text-xs ${active ? '' : 'text-gray-300'}`}>
        {active ? (desc ? '▼' : '▲') : '↕'}
      </span>
    </button>
  );
}

/**
 * Props for a `SortHeader` driven by a "field" / "-field" sort token: first
 * click sorts ascending, a second click on the same column flips to descending.
 */
export function sortHeaderProps<F extends string>(
  current: F | `-${F}` | undefined,
  field: F,
  onSort: (token: F | `-${F}`) => void,
) {
  const desc = current === `-${field}`;
  return {
    active: current === field || desc,
    desc,
    onClick: () => onSort(current === field ? `-${field}` : field),
  };
}
